import React, { useState, useEffect } from "react";
import { Row, Col, Card, Tab, Nav, Modal, Button, Spinner, Form, Alert } from "react-bootstrap";
import { Information } from "iconsax-react";
import { useNavigate, useParams } from 'react-router-dom';
import MyApiClient from "../../../../../api-client/MyApiClient";
import Select from "react-select";
import { sidSelectStyles } from "../../../../components/Util/sidSelectStyles";
import { sports, bookingSubscriptionExpirationType, tipologiaStruttura } from "../../../../components/Util/enum";
import { useProfile } from "../../../../../hooks/useProfile";
import { useFederations } from "../../../../../hooks/useFederations";
import ProductImageUploader from "../../../../components/Util/ProductImageUploader";
import { VAT_OPTIONS, findVatOption, vatFieldsFromOption } from "../../../../components/Util/vatOptions";
import { conMotivo } from "../../../../components/Util/erroreServer";
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";
import { useStatoNelTitolo } from "../../../../components/Util/StatoProdotto";

/*
 * Nuovo abbonamento e bozza di un abbonamento: una pagina sola, con lo stesso aspetto della scheda
 * (view.js): un box a sezioni (Informazioni, Privilegi, Prezzi, Forme di pagamento). Prima era un
 * percorso a quattro passi (Informazioni -> Prezzi -> Condizioni -> Riepilogo).
 * Senza id nell'indirizzo crea l'abbonamento; con l'id (subscriptionsedit/:id) apre la bozza.
 * "Salva bozza" chiede solo il nome; "Salva e attiva" fa tutti i controlli del vecchio percorso insieme.
 */
export const SubscriptionsNew = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const bozzaEsistente = !!id;

    const { profile: profileData } = useProfile({
        autoFetch: true,
        includeTitles: true
    });

    const { federations } = useFederations();

    const sdiEnabledNoVat = profileData?.club?.invoiceDataSender?.invoice_enabled === true && !profileData?.club?.invoiceDataSender?.vat_number;
    const billingNotConfirmed = profileData && profileData?.club?.invoiceDataSender?.status !== 'CONFIRMED';
    const prezzoBloccato = sdiEnabledNoVat || billingNotConfirmed;

    const [loadingShowed, setLoadingShowed] = useState(false);

    const [confirmShowed, setConfirmShowed] = useState(false);
    const [onContinue, setOnContinue] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    const [pictureModalShowed, setPictureModalShowed] = useState(false);

    const [enabledSports, setEnabledSports] = useState([]);

    const [modalShowed, setModalShowed] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalText, setModalText] = useState('');

    const [showingTesseratiOptions, setShowingTesseratiOptions] = useState(false);
    // Chi tiene il libro soci puo' anche vendere l'abbonamento che rende soci:
    // associazioni, societa' sportive dilettantistiche e societa' di capitali.
    const [showingSociOptions, setShowingSociOptions] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState({
        stripe_enabled: false,
        bank_transfer_enabled: false
    });

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        sportType: '',
        expiration_type: '',
        expiration_date: '',
        description: '',
        standard_price: 0,
        member_price: 0,
        federated_price: 0,
        fitcoin_cashback: 0,
        payable_in_club: false,
        payable_stripe: false,
        payable_bank: false,
        vat_rate: 22,
        vat_nature: undefined,
        iva_5_percent: false,
        auto_renew: false,
        grants_club_membership: false,
        grants_federated_status: false,
        federation_code: '',
        newSubscriptionPicture: '',
        subscriptionPicture: ''
    });

    // Nome com'e' stato caricato: il titolo non cambia mentre lo si modifica
    const [loadedName, setLoadedName] = useState('');
    usePageTitle(bozzaEsistente ? (loadedName || null) : null);
    useStatoNelTitolo({ visibile: bozzaEsistente && !!loadedName, attivo: false, bozza: true });

    useEffect(() => {
        if (profileData && profileData.club && profileData.club.sports) {
            const clubSports = profileData.club.sports.map(s => s.sportType);
            setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
        }
    }, [profileData]);

    useEffect(() => {
        const tokenDetailsString = sessionStorage.getItem('userDetails');
        if (tokenDetailsString) {
            const tokenDetails = JSON.parse(tokenDetailsString);
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value || tokenDetails.clubNature === tipologiaStruttura.SSD.value) {
                setShowingTesseratiOptions(true);
            }
            if (tokenDetails.clubNature === tipologiaStruttura.ASD.value
                || tokenDetails.clubNature === tipologiaStruttura.SSD.value
                || tokenDetails.clubNature === tipologiaStruttura.CAPITAL_COMPANY.value) {
                setShowingSociOptions(true);
            }
            setPaymentMethods({
                stripe_enabled: tokenDetails.stripe_enabled || false,
                bank_transfer_enabled: tokenDetails.bank_transfer_enabled || false
            });
        }
    }, []);

    // Bozza esistente: si caricano i suoi dati
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
                if (!token) return;
                setLoadingShowed(true);
                const abbonamento = await new MyApiClient().getBookingSubscriptionById(token, id);
                setLoadingShowed(false);
                if (abbonamento) {
                    setFormData({
                        id: abbonamento.id,
                        name: abbonamento.name,
                        sportType: abbonamento.sportType,
                        expiration_type: abbonamento.expiration_type,
                        expiration_date: abbonamento.expiration_date || '',
                        description: abbonamento.description || '',
                        standard_price: abbonamento.standard_price,
                        member_price: abbonamento.member_price,
                        federated_price: abbonamento.federated_price,
                        fitcoin_cashback: abbonamento.fitcoin_cashback,
                        payable_in_club: abbonamento.payable_in_club,
                        payable_stripe: abbonamento.payable_stripe,
                        payable_bank: abbonamento.payable_bank,
                        vat_rate: abbonamento.vat_rate,
                        vat_nature: abbonamento.vat_nature,
                        iva_5_percent: abbonamento.iva_5_percent,
                        auto_renew: abbonamento.auto_renew,
                        grants_club_membership: abbonamento.grants_club_membership,
                        grants_federated_status: abbonamento.grants_federated_status,
                        federation_code: abbonamento.federation_code || '',
                        newSubscriptionPicture: '',
                        subscriptionPicture: abbonamento.subscriptionPicture
                    });
                    setLoadedName(abbonamento.name || '');
                }
            } catch (error) {
                setLoadingShowed(false);
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (selectedOption, name) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption?.value || '' }));
    };

    const mostraErrore = (titolo, testo) => {
        setModalTitle(titolo);
        setModalText(testo);
        setModalShowed(true);
    };

    const getAvailableFederations = () => {
        if (!formData.sportType || !federations.length) return [];

        // Organismi configurati dalla struttura per questo sport
        const clubSport = profileData?.club?.sports?.find(s => s.sportType === formData.sportType);
        const clubOrganisms = clubSport?.sportPromotionBody || [];

        if (clubOrganisms.length === 0) return [];

        return federations
            .filter(fed => clubOrganisms.includes(fed.code))
            .map(fed => ({
                value: fed.code,
                label: `${fed.code} - ${fed.description}`
            }));
    };
    const availableFederations = getAvailableFederations();

    // L'immagine viaggia come { content, name }: la si invia solo se ne e' stata
    // scelta una nuova, altrimenti si omette il campo (un content null oppure un
    // oggetto gia' incapsulato faceva fallire lo schema con "must be string").
    const preparePicturePayload = (payload, field) => {
        const current = payload[field];
        const content = typeof current === 'string' ? current : current?.content;
        if (content) {
            payload[field] = {
                content: content,
                name: Math.random().toString(36).substring(2, 10) + (new Date()).getTime().toString(36),
            };
        } else {
            delete payload[field];
        }
    };

    // L'abbonamento come lo vuole il server, sempre in bozza: l'attivazione e' una chiamata a parte.
    const payloadBozza = () => {
        const tokenDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
        const payload = { ...formData, clubId: tokenDetails.localId, status: 'DRAFT' };
        if (!bozzaEsistente) delete payload.id;

        if (!showingSociOptions) {
            payload.grants_club_membership = false;
        }
        payload.member_price = payload.standard_price;
        if (!showingTesseratiOptions) {
            payload.grants_federated_status = false;
            payload.federated_price = payload.standard_price;
        }
        if (!payload.grants_federated_status) {
            payload.federation_code = '';
        }

        preparePicturePayload(payload, 'newSubscriptionPicture');
        Object.keys(payload).forEach(key => { if (payload[key] === '') delete payload[key]; });
        return payload;
    };

    // Crea o aggiorna la bozza; torna l'id dell'abbonamento, oppure null se non riesce (errore gia' mostrato)
    const registraBozza = async (token) => {
        const apiClient = new MyApiClient();
        const payload = payloadBozza();
        const result = bozzaEsistente
            ? await apiClient.updateBookingSubscription(token, payload)
            : await apiClient.createBookingSubscription(token, payload);
        if (!result || result.error) {
            mostraErrore("Errore durante l'operazione", result?.error || conMotivo("Si è verificato un errore durante il salvataggio"));
            return null;
        }
        return bozzaEsistente ? id : result.data?.id;
    };

    const salvaBozza = async () => {
        if (!formData.name || formData.name.trim() === '') {
            mostraErrore("Dati mancanti", "Devi inserire il nome per salvare la bozza.");
            return;
        }
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            setLoadingShowed(true);
            const salvato = await registraBozza(token);
            setLoadingShowed(false);
            if (salvato) navigate('/struttura/booking/subscriptions');
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in salvaBozza: ' + error);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio della bozza"));
        }
    };

    // Gli stessi controlli del vecchio percorso a passi, tutti insieme: il primo che manca viene detto in chiaro.
    const cosaManca = () => {
        const numero = (v) => parseFloat(String(v ?? '').replace(',', '.'));
        if (!formData.name || !formData.name.trim()) return "Inserisci il nome dell'abbonamento.";
        if (!formData.sportType) return "Scegli lo sport.";
        if (!formData.expiration_type) return "Scegli la durata.";
        if (formData.expiration_type === 'FIXED_DATE') {
            if (!formData.expiration_date) return "Per la scadenza a data fissa è obbligatorio indicare la data di fine validità.";
            if (formData.expiration_date <= new Date().toISOString().slice(0, 10)) return "La data di fine validità deve essere futura.";
        }
        if (!formData.description || !formData.description.trim()) return "Inserisci la descrizione.";
        if (!formData.newSubscriptionPicture && !formData.subscriptionPicture) return "Carica la foto dell'abbonamento.";
        if (showingTesseratiOptions && formData.grants_federated_status && !formData.federation_code) {
            return "Se l'abbonamento rende tesserato nello sport, è obbligatorio selezionare un organismo.";
        }
        if (!prezzoBloccato && !(numero(formData.standard_price) > 0)) return "Il prezzo standard deve essere maggiore di 0.";
        if (showingTesseratiOptions && !(numero(formData.federated_price) > 0)) return "Il prezzo tesserati / soci deve essere maggiore di 0.";
        if (!(numero(formData.fitcoin_cashback) >= 0)) return "Il cashback Fitcoin deve essere maggiore o uguale a 0.";
        if (!formData.payable_in_club && !formData.payable_stripe && !formData.payable_bank) {
            return "Almeno uno dei metodi di pagamento deve essere attivo (in struttura, Stripe o bonifico).";
        }
        return null;
    };

    const salvaEAttiva = () => {
        const manca = cosaManca();
        if (manca) {
            mostraErrore("Dati mancanti", manca);
            return;
        }
        setOnContinue(() => () => continuaSalvaEAttiva());
        setConfirmShowed(true);
        setConfirmText("Sei sicuro di voler salvare e attivare questo abbonamento? Dopo l'attivazione potrai cambiare solo nome, descrizione, prezzi e forme di pagamento.");
    };

    const continuaSalvaEAttiva = async () => {
        setConfirmShowed(false);
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            setLoadingShowed(true);
            const abbonamentoId = await registraBozza(token);
            if (!abbonamentoId) {
                setLoadingShowed(false);
                return;
            }
            const attivazione = await new MyApiClient().changeSubscriptionStatus(token, abbonamentoId, 'PURCHASABLE');
            setLoadingShowed(false);
            if (!attivazione || attivazione.error) {
                mostraErrore("Errore durante l'attivazione", attivazione?.error || "L'abbonamento è stato salvato come bozza ma non è stato possibile attivarlo.");
            } else {
                navigate('/struttura/booking/subscriptions');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaSalvaEAttiva: ' + error);
            mostraErrore("Errore durante l'operazione", conMotivo("Si è verificato un errore durante il salvataggio e attivazione"));
        }
    };

    // Eliminazione della bozza: il server la cancella finche' nessuno l'ha acquistata.
    const eliminaBozza = () => {
        setOnContinue(() => () => continuaEliminaBozza());
        setConfirmShowed(true);
        setConfirmText("Sei sicuro di volere continuare? La bozza dell'abbonamento sarà eliminata dalla tua struttura.");
    };

    const continuaEliminaBozza = async () => {
        setConfirmShowed(false);
        try {
            const token = JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
            if (!token) return;
            setLoadingShowed(true);
            const result = await new MyApiClient().deleteBookingSubscription(token, id);
            setLoadingShowed(false);
            if (!result || result.error) {
                mostraErrore("Eliminazione non riuscita", result && result.error ? result.error : conMotivo("Si è verificato un errore durante la cancellazione"));
            } else {
                navigate('/struttura/booking/subscriptions');
            }
        } catch (error) {
            setLoadingShowed(false);
            console.error('Error in continuaEliminaBozza: ' + error);
            mostraErrore("Eliminazione non riuscita", conMotivo("Si è verificato un errore durante la cancellazione"));
        }
    };

    const interruttore = (fieldName, label) => (
        <div className="sid-switch-row">
            <Form.Check
                type="switch"
                id={`switch-${fieldName}`}
                label={label}
                checked={!!formData[fieldName]}
                onChange={(e) => setFormData(prev => ({ ...prev, [fieldName]: e.target.checked }))}
            />
        </div>
    );

    const foto = formData.newSubscriptionPicture
        ? `data:image/jpeg;base64,${formData.newSubscriptionPicture}`
        : (formData.subscriptionPicture || null);

    const rimuoviFoto = () => setFormData(prev => ({ ...prev, newSubscriptionPicture: '', subscriptionPicture: '' }));

    const showingPrivileges = showingTesseratiOptions || showingSociOptions;

    return (
        <>
            <ProductImageUploader
                show={pictureModalShowed}
                onClose={() => setPictureModalShowed(false)}
                onSave={(base64String) => {
                    setPictureModalShowed(false);
                    setFormData(prev => ({ ...prev, newSubscriptionPicture: base64String }));
                }}
                titolo="Immagine dell'abbonamento"
            />

            <Modal className="fade" show={confirmShowed} centered>
                <Modal.Header>
                    <Modal.Title>Sei sicuro?</Modal.Title>
                    <Button onClick={() => setConfirmShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col>{confirmText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <Button className="btn btn-light btn-add" onClick={() => setConfirmShowed(false)}>Chiudi</Button>
                    <Button className="btn btn-primary btn-add" onClick={() => { setConfirmShowed(false); if (onContinue) onContinue(); }}>Continua</Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={modalShowed} centered>
                <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                    <Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
                </Modal.Header>
                <Modal.Body>
                    <Row><Col>{modalText}</Col></Row>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
                </Modal.Footer>
            </Modal>

            <Modal className="fade" show={loadingShowed} centered>
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                    <div>Caricamento in corso...</div>
                </Modal.Body>
            </Modal>

            {billingNotConfirmed && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 text-dark" style={{ marginBottom: '1rem' }}>
                    <Information size="20" className="mt-1 flex-shrink-0" />
                    <div>
                        <strong>Metodo di fatturazione non configurato.</strong> Per pubblicare prodotti e renderli acquistabili nell'App SportId Player è necessario configurare e confermare il metodo di fatturazione nella sezione <strong>La mia struttura → Documenti fiscali</strong>. Puoi continuare a creare prodotti e salvarli come bozza.
                    </div>
                </Alert>
            )}

            <div className="custom-tab-1">
                <Tab.Container defaultActiveKey="informazioni">
                    <Nav as="ul" className="nav-tabs" style={{ border: 'none' }}>
                        <Nav.Item as="li" className="tabMenu">
                            <Nav.Link eventKey="informazioni">Informazioni generali</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Row className="align-items-start mt-4">
                        <div className="col-12">
                            <Tab.Content>
                                <Tab.Pane eventKey="informazioni">
                                    <Card className="sid-scheda-prodotto">
                                        <Card.Body className="sid-card-body--spaced">
                                            <Row className="g-4">
                                                <Col md={6}>
                                                    <Form.Label className="mb-2">Nome abbonamento *</Form.Label>
                                                    <Form.Control className="mt-2" type="text" name="name" value={formData.name} onChange={handleChange} />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Sport *</Form.Label>
                                                    <Select
                                                        className="mt-2"
                                                        styles={sidSelectStyles}
                                                        placeholder=""
                                                        name="sportType"
                                                        value={enabledSports.find(option => option.value === formData.sportType) || null}
                                                        onChange={(selectedOption) => handleSelectChange(selectedOption, "sportType")}
                                                        options={enabledSports}
                                                    />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Durata *</Form.Label>
                                                    <Select
                                                        className="mt-2"
                                                        styles={sidSelectStyles}
                                                        placeholder=""
                                                        name="expiration_type"
                                                        value={bookingSubscriptionExpirationType.find(option => option.value === formData.expiration_type) || null}
                                                        onChange={(selectedOption) => handleSelectChange(selectedOption, "expiration_type")}
                                                        options={bookingSubscriptionExpirationType}
                                                    />
                                                </Col>
                                                {formData.expiration_type === 'FIXED_DATE' && (
                                                    <Col md={{ span: 3, offset: 9 }}>
                                                        <Form.Label className="mb-2">Data di fine validità *</Form.Label>
                                                        <Form.Control
                                                            className="mt-2"
                                                            type="date"
                                                            name="expiration_date"
                                                            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                                                            value={formData.expiration_date}
                                                            onChange={handleChange}
                                                        />
                                                        <Form.Text className="d-block mt-2">
                                                            L'abbonamento decorre dal momento dell'acquisto e scade in questa data.
                                                        </Form.Text>
                                                    </Col>
                                                )}
                                                <Col md={12}>
                                                    <Form.Label className="mb-2">Descrizione *</Form.Label>
                                                    <Form.Control className="mt-2" rows={10} as="textarea" name="description" value={formData.description || ''} onChange={handleChange} />
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Label>Foto *</Form.Label>
                                                    <div className="sid-photo-field">
                                                        <div className="sid-photo-field__preview">
                                                            {foto ? <img src={foto} alt="" /> : <span>Nessuna foto</span>}
                                                        </div>
                                                        <Button size="sm" variant="outline-dark" onClick={() => setPictureModalShowed(true)}>
                                                            {foto ? 'Sostituisci foto' : 'Carica foto'}
                                                        </Button>
                                                        {foto && (
                                                            <Button size="sm" variant="outline-danger" onClick={rimuoviFoto}>Rimuovi</Button>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>

                                            {showingPrivileges && (
                                                <>
                                                    <h5 className="sid-form-section">Privilegi</h5>
                                                    <div className="sid-switch-inline">
                                                        {showingTesseratiOptions && interruttore("grants_federated_status", "Rende tesserato nello sport")}
                                                        {showingSociOptions && interruttore("grants_club_membership", "Rende socio")}
                                                    </div>
                                                    {showingSociOptions && (
                                                        <Form.Text className="d-block mt-2">
                                                            Chi acquista entra nel libro soci quando l'ordine risulta pagato. Se il player
                                                            era gia' stato ammesso dall'assemblea, l'acquisto ne salda la quota.
                                                            <br />
                                                            <strong>Gli abbonamenti che rendono socio non compaiono nel catalogo dell'app:</strong> si
                                                            vendono dal Manager, in vendita manuale.
                                                        </Form.Text>
                                                    )}
                                                    {showingTesseratiOptions && formData.grants_federated_status && (
                                                        <Row className="g-4 mt-0">
                                                            <Col md={6}>
                                                                <Form.Label className="mb-2">Organismo *</Form.Label>
                                                                <Select
                                                                    className="mt-2"
                                                                    styles={sidSelectStyles}
                                                                    placeholder=""
                                                                    name="federation_code"
                                                                    value={availableFederations.find(option => option.value === formData.federation_code) || null}
                                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, "federation_code")}
                                                                    options={availableFederations}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    )}
                                                </>
                                            )}

                                            <h5 className="sid-form-section">Prezzi</h5>
                                            <Row className="g-4">
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Prezzo standard *</Form.Label>
                                                    <Form.Control
                                                        disabled={prezzoBloccato}
                                                        className={`mt-2${prezzoBloccato ? ' sid-ro-input' : ''}`}
                                                        type="text"
                                                        inputMode="decimal"
                                                        name="standard_price"
                                                        value={formData.standard_price}
                                                        onChange={handleChange}
                                                    />
                                                </Col>
                                                {!formData.grants_federated_status && (
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2">IVA</Form.Label>
                                                        <Select
                                                            className="mt-2"
                                                            styles={sidSelectStyles}
                                                            isDisabled={prezzoBloccato}
                                                            name="vat_rate"
                                                            value={findVatOption(formData)}
                                                            onChange={(selectedOption) => setFormData(prev => ({ ...prev, ...vatFieldsFromOption(selectedOption) }))}
                                                            options={VAT_OPTIONS}
                                                        />
                                                    </Col>
                                                )}
                                                {showingTesseratiOptions && (
                                                    <Col md={3}>
                                                        <Form.Label className="mb-2">Prezzo tesserati / soci *</Form.Label>
                                                        <Form.Control className="mt-2" type="text" inputMode="decimal" name="federated_price" value={formData.federated_price} onChange={handleChange} />
                                                    </Col>
                                                )}
                                                <Col md={3}>
                                                    <Form.Label className="mb-2">Cashback Fitcoin *</Form.Label>
                                                    <Form.Control className="mt-2" type="number" min="0" step="0.01" name="fitcoin_cashback" value={formData.fitcoin_cashback} onChange={handleChange} />
                                                </Col>
                                            </Row>

                                            <h5 className="sid-form-section">Forme di pagamento</h5>
                                            <div className="sid-switch-inline">
                                                {interruttore("payable_in_club", "Acquisto pagabile in struttura")}
                                                {paymentMethods.stripe_enabled && interruttore("payable_stripe", "Acquisto pagabile su Stripe")}
                                                {paymentMethods.bank_transfer_enabled && interruttore("payable_bank", "Acquisto pagabile con bonifico")}
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <div className={`d-flex ${bozzaEsistente ? 'justify-content-between' : 'justify-content-end'} align-items-center gap-4 mt-4`}>
                                        {bozzaEsistente && (
                                            <button type="button" className="btn btn-danger light" onClick={eliminaBozza}>Elimina bozza</button>
                                        )}
                                        <div className="d-flex gap-4">
                                            <button type="button" className="btn btn-dark light" onClick={() => navigate('/struttura/booking/subscriptions')}>
                                                {bozzaEsistente ? 'Indietro' : 'Annulla'}
                                            </button>
                                            <button type="button" className="btn btn-secondary" onClick={salvaBozza}>Salva bozza</button>
                                            {!billingNotConfirmed && (
                                                <button type="button" className="btn btn-primary" onClick={salvaEAttiva}>Salva e attiva</button>
                                            )}
                                        </div>
                                    </div>
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </Row>
                </Tab.Container>
            </div>
        </>
    );
};
export default SubscriptionsNew;
